import { useState, useEffect } from "react";
import { Alert, Box, Button, IconButton, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadIcon from "@mui/icons-material/Upload";
import { useSearchParams } from "react-router-dom";
import PageContainer from "../page-container/page-container";
import { useNotification } from "../../context/notification-provider";
import useClients from "../client-config/data/use-clients";
import { ClientInformation } from "../client-config/data/types";
import FileDropzone from "../dropzone/file-dropzone";
import FileList from "../dropzone/file-list";
import { UploadedFile } from "../dropzone/types";
import AutoComplete from "../client-information/auto-complete";
import uploadFileWithProgress from "./service";
import LoadingScreen from "../progress/loading-screen";

const ClientUpload = () => {
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedClient, setSelectedClient] =
    useState<ClientInformation | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingError, setUploadingError] = useState(false);

  const { data, isLoading, error } = useClients();
  const filteredClients = data?.filter(
    (client) => client.enabled && !client.deleted,
  );

  // Pre-select client from URL parameter
  useEffect(() => {
    const clientId = searchParams.get("clientId");
    if (clientId && filteredClients) {
      const client = filteredClients.find(
        (c) => c.id === Number.parseInt(clientId, 10),
      );
      if (client) {
        setSelectedClient(client);
      }
    }
  }, [searchParams, filteredClients]);

  const addFiles = (files: File[]) => {
    const newFiles: UploadedFile[] = files.map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}`,
      file,
      uploadedAt: new Date(),
      lastModifiedDate: new Date(file.lastModified),
      sharedWithOrg: false,
      progress: 0,
    }));

    if (uploadingError) {
      setUploadingError(false);
      setIsUploading(false);
    }

    setUploadedFiles(newFiles);
  };

  const removeFile = (id: string) => {
    if (isUploading) return;

    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));

    if (uploadedFiles.length === 1 && uploadedFiles[0].id === id) {
      setUploadingError(false);
    }
  };

  const handleUploadErrors = (
    errors: { fileId: string; message: string }[],
  ) => {
    setUploadedFiles((prev) =>
      prev.map((file) => {
        const err = errors.find((e) => e.fileId === file.id);
        return err
          ? {
              ...file,
              progress: 0,
              uploadError: true,
              uploadErrorMessage: err.message,
            }
          : file;
      }),
    );

    setUploadingError(true);

    errors.forEach((err) => {
      showNotification({
        message: `Error uploading file: ${err.message}`,
        severity: "error",
        duration: 3000,
      });
    });
  };

  const onUpload = async () => {
    if (!uploadedFiles.length || !selectedClient) return;

    setIsUploading(true);

    showNotification({
      message: `Uploading ${uploadedFiles.length} file(s)...`,
      severity: "info",
      duration: 2500,
    });

    const results = await Promise.all(
      uploadedFiles.map((file) =>
        uploadFileWithProgress(
          file.id,
          file.file,
          selectedClient?.id,
          setUploadedFiles,
        ),
      ),
    );

    const errors = results.filter((r) => r !== null) as {
      fileId: string;
      message: string;
    }[];

    if (errors.length === 0) {
      showNotification({
        message: "Upload completed successfully.",
        severity: "success",
        duration: 3000,
      });
      setIsUploading(false);
      return;
    }

    handleUploadErrors(errors);
    setIsUploading(false);
  };

  const handleRefresh = () => {
    setSelectedClient(null);
    setUploadedFiles([]);
    setUploadingError(false);
    setIsUploading(false);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">Unable to load client information</Alert>
      </Box>
    );
  }

  return (
    <PageContainer
      title="File Upload"
      actions={
        <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
          <RefreshIcon />
        </IconButton>
      }
    >
      <Box sx={{ px: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: "text.secondary",
            letterSpacing: 0.4,
            textTransform: "uppercase",
          }}
        >
          Client
        </Typography>

        <AutoComplete
          suggestions={filteredClients || []}
          label="Select Client"
          selectedValue={selectedClient}
          onChange={setSelectedClient}
          sx={{
            width: "100%",
            "& .MuiAutocomplete-input": {
              padding: "10px 12px",
              fontSize: "0.95rem",
              fontWeight: selectedClient ? 600 : 400,
            },
          }}
        />

        <Typography
          variant="subtitle2"
          sx={{
            mt: 3,
            mb: 1,
            fontWeight: 600,
            color: "text.secondary",
            letterSpacing: 0.4,
            textTransform: "uppercase",
          }}
        >
          Files
        </Typography>

        <FileDropzone
          files={uploadedFiles}
          onFilesAdded={addFiles}
          disabled={isUploading}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<UploadIcon />}
          disabled={
            uploadedFiles.length === 0 ||
            isUploading ||
            uploadingError ||
            !selectedClient
          }
          onClick={onUpload}
          sx={{
            mt: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            py: 1.5,
            boxShadow: "0 3px 6px rgb(25 118 210 / 0.3)",
            "&:hover": {
              boxShadow: "0 5px 10px rgb(25 118 210 / 0.5)",
            },
          }}
        >
          {isUploading
            ? "Uploading..."
            : `Upload ${uploadedFiles.length} file${
                uploadedFiles.length !== 1 ? "s" : ""
              }`}
        </Button>

        {uploadedFiles.length > 0 && (
          <FileList files={uploadedFiles} onRemove={removeFile} />
        )}
      </Box>
    </PageContainer>
  );
};

export default ClientUpload;
